/* eslint-disable @typescript-eslint/no-empty-function */
import { ChangeDetectionStrategy, ContentChildren, Component, Directive, EventEmitter, forwardRef, Input, Optional, Output, ViewChild, } from '@angular/core';
import { provideValueAccessor, TemplateUuids } from '@mhe/ngx-shared/utils';
import * as i0 from "@angular/core";
/**
 * @deprecated since version 9.0
 */
export class RadioGroupDirective {
    _changeDetector;
    templateUuids = new TemplateUuids();
    _required = false;
    set required(value) {
        this._required = typeof value !== 'undefined' && value !== false;
    }
    get required() {
        if (this._required) {
            return true;
        }
        let isRequired = false;
        this.radioButtons?.forEach((radio, index) => {
            if (radio._required) {
                isRequired = true;
            }
        });
        return isRequired;
    }
    _disabled = false;
    get disabled() {
        return this._disabled;
    }
    set disabled(value) {
        this._disabled = !!value;
        this._markRadiosForCheck();
    }
    _selected = null;
    get selected() {
        return this._selected;
    }
    set selected(selected) {
        this._selected = selected;
        this.value = selected ? selected.value : null;
        this._checkSelectedRadioButton();
    }
    _value = null;
    get value() {
        return this._value;
    }
    set value(newValue) {
        if (this._value !== newValue) {
            // Set this before proceeding to ensure no circular loop occurs with selection.
            this._value = newValue;
            this._updateSelectedRadioFromValue();
            this._checkSelectedRadioButton();
        }
    }
    radioGroupChange = new EventEmitter();
    radioButtons;
    constructor(_changeDetector) {
        this._changeDetector = _changeDetector;
    }
    ngAfterContentInit() {
        const uuid = this.templateUuids.get('radioName');
        this.radioButtons.forEach((radio, index) => {
            radio._inputElement.nativeElement.name = uuid;
        });
        this._changeDetector.detectChanges();
    }
    change(radioButton) {
        this.radioGroupChange.emit(radioButton);
    }
    _checkSelectedRadioButton() {
        if (this._selected && !this._selected.checked) {
            this._selected.checked = true;
        }
    }
    _markRadiosForCheck() {
        if (this.radioButtons) {
            this.radioButtons.forEach((radio) => radio._markForCheck());
        }
    }
    onChangeCallback = (_) => { };
    onTouchedCallback = () => { };
    registerOnChange(fn) {
        this.onChangeCallback = fn;
    }
    registerOnTouched(fn) {
        this.onTouchedCallback = fn;
    }
    setDisabledState(isDisabled) {
        this.disabled = isDisabled;
        this._changeDetector.markForCheck();
    }
    _updateSelectedRadioFromValue() {
        // If the value already matches the selected radio, do nothing.
        const isAlreadySelected = this._selected != null && this._selected.value === this._value;
        if (this.radioButtons != null && !isAlreadySelected) {
            this._selected = null;
            this.radioButtons.forEach((radio) => {
                radio.checked = this.value === radio.value;
                if (radio.checked) {
                    this._selected = radio;
                }
            });
        }
    }
    writeValue(val) {
        if (val !== this.value) {
            this.value = val;
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: RadioGroupDirective, deps: [{ token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.4.0", type: RadioGroupDirective, selector: "[mheRadioGroup]", inputs: { required: "required", disabled: "disabled", selected: "selected", value: "value" }, outputs: { radioGroupChange: "radioGroupChange" }, providers: [provideValueAccessor(RadioGroupDirective)], queries: [{ propertyName: "radioButtons", predicate: i0.forwardRef(function () { return RadioButtonComponent; }), descendants: true }], exportAs: ["mheRadioGroup"], ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: RadioGroupDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[mheRadioGroup]',
                    exportAs: 'mheRadioGroup',
                    providers: [provideValueAccessor(RadioGroupDirective)],
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }]; }, propDecorators: { required: [{
                type: Input
            }], disabled: [{
                type: Input
            }], selected: [{
                type: Input
            }], value: [{
                type: Input
            }], radioGroupChange: [{
                type: Output
            }], radioButtons: [{
                type: ContentChildren,
                args: [forwardRef(() => RadioButtonComponent), { descendants: true }]
            }] } });
/**
 * @deprecated since version 9.0
 */
export class RadioButtonComponent {
    radioGroup;
    _changeDetector;
    _required = false;
    set required(value) {
        this._required = typeof value !== 'undefined' && value !== false;
    }
    get required() {
        return this._required;
    }
    get checked() {
        return this._checked;
    }
    set checked(value) {
        const newCheckedState = !!value;
        if (this._checked !== newCheckedState) {
            this._checked = newCheckedState;
            this._inputElement.nativeElement.checked = this._checked;
            if (newCheckedState && this.radioGroup && this.radioGroup.value !== this.value) {
                this.radioGroup.selected = this;
            }
            else if (!newCheckedState && this.radioGroup && this.radioGroup.value === this.value) {
                this.radioGroup.selected = null;
            }
            if (newCheckedState && this.radioGroup && this.radioGroup.radioButtons) {
                this.radioGroup.radioButtons.forEach((radioButton) => {
                    if (radioButton !== this) {
                        radioButton.checked = false;
                    }
                });
            }
            this._changeDetector.markForCheck();
        }
    }
    get disabled() {
        return this._disabled || (this.radioGroup !== null && this.radioGroup.disabled);
    }
    set disabled(value) {
        const newDisabledState = !!value;
        if (this._disabled !== newDisabledState) {
            this._disabled = newDisabledState;
            this._changeDetector.markForCheck();
        }
    }
    get value() {
        return this._value;
    }
    set value(value) {
        if (this._value !== value) {
            this._value = value;
            if (this.radioGroup != null) {
                if (!this.checked) {
                    this.checked = this.radioGroup.value === value;
                }
                if (this.checked) {
                    this.radioGroup.selected = this;
                }
            }
        }
    }
    // eslint-disable-next-line @angular-eslint/no-output-native
    change = new EventEmitter();
    _inputElement;
    _checked = false;
    _disabled = false;
    _value = null;
    constructor(radioGroup, _changeDetector) {
        this.radioGroup = radioGroup;
        this._changeDetector = _changeDetector;
    }
    inputChange(event) {
        event.stopPropagation();
        // Sync the value from the underlying input element with the component instance.
        this.checked = this._inputElement.nativeElement.checked;
        this.change.emit();
        if (this.radioGroup) {
            this.radioGroup.change(this);
        }
    }
    _markForCheck() {
        this._changeDetector.markForCheck();
    }
    setDisabledState(isDisabled) {
        this.disabled = isDisabled;
        this._changeDetector.markForCheck();
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: RadioButtonComponent, deps: [{ token: RadioGroupDirective, optional: true }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.4.0", type: RadioButtonComponent, selector: "mhe-radio-button", inputs: { required: "required", checked: "checked", disabled: "disabled", value: "value" }, outputs: { change: "change" }, viewQueries: [{ propertyName: "_inputElement", first: true, predicate: ["input"], descendants: true, static: true }], ngImport: i0, template: "<label class=\"ahe-ui-radio\">\n  <input\n    #input\n    type=\"radio\"\n    (change)=\"inputChange($event)\"\n    [attr.disabled]=\"disabled ? '' : null\"\n    [attr.required]=\"required ? '' : null\"\n  />\n  <span><ng-content></ng-content></span>\n</label>\n", styles: ["\n"], changeDetection: i0.ChangeDetectionStrategy.OnPush });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: RadioButtonComponent, decorators: [{
            type: Component,
            args: [{ selector: 'mhe-radio-button', changeDetection: ChangeDetectionStrategy.OnPush, template: "<label class=\"ahe-ui-radio\">\n  <input\n    #input\n    type=\"radio\"\n    (change)=\"inputChange($event)\"\n    [attr.disabled]=\"disabled ? '' : null\"\n    [attr.required]=\"required ? '' : null\"\n  />\n  <span><ng-content></ng-content></span>\n</label>\n", styles: ["/*# sourceMappingURL=radio-button.component.css.map */\n"] }]
        }], ctorParameters: function () { return [{ type: RadioGroupDirective, decorators: [{
                    type: Optional
                }] }, { type: i0.ChangeDetectorRef }]; }, propDecorators: { required: [{
                type: Input
            }], checked: [{
                type: Input
            }], disabled: [{
                type: Input
            }], value: [{
                type: Input
            }], change: [{
                type: Output
            }], _inputElement: [{
                type: ViewChild,
                args: ['input', { static: true }]
            }] } });
